import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";

interface EnhancedSignupFormProps {
  email: string;
  prefilledData?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    jobTitle?: string;
  };
  onSuccess: (formData?: {
    firstName: string;
    lastName: string;
    phone: string;
    jobTitle: string;
  }) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  customButtonClass?: string;
}

export function EnhancedSignupForm({
  email,
  prefilledData,
  onSuccess,
  isProcessing,
  setIsProcessing,
  customButtonClass = ""
}: EnhancedSignupFormProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState(prefilledData?.firstName || "");
  const [lastName, setLastName] = useState(prefilledData?.lastName || "");
  const [phone, setPhone] = useState(prefilledData?.phone || "");
  const [jobTitle, setJobTitle] = useState(prefilledData?.jobTitle || "");
  const [invitationData, setInvitationData] = useState<any>(null);
  const { toast } = useToast();

  // Password validation
  const validatePassword = (pwd: string) => {
    const requirements = {
      minLength: pwd.length >= 10,
      hasLowercase: /[a-z]/.test(pwd),
      hasUppercase: /[A-Z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
      isPassphrase: pwd.length >= 16
    };

    const score = Object.values(requirements).filter(Boolean).length;
    const isStrong = requirements.minLength && 
      (requirements.isPassphrase || 
       (requirements.hasLowercase && requirements.hasUppercase && 
        requirements.hasNumber && requirements.hasSpecial));

    return { requirements, score, isStrong };
  };

  const passwordValidation = validatePassword(password);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const canSubmit = passwordValidation.isStrong && passwordsMatch && !isProcessing;

  useEffect(() => {
    // Set default username from email prefix
    if (email && !username) {
      const emailPrefix = email.split('@')[0];
      setUsername(emailPrefix);
    }
  }, [email]);

  // React to prefilledData prop changes (for when partner details load later)
  useEffect(() => {
    if (prefilledData?.firstName && !firstName) setFirstName(prefilledData.firstName);
    if (prefilledData?.lastName && !lastName) setLastName(prefilledData.lastName);
    if (prefilledData?.phone && !phone) setPhone(prefilledData.phone);
    if (prefilledData?.jobTitle && !jobTitle) setJobTitle(prefilledData.jobTitle);
  }, [prefilledData]);

  useEffect(() => {
    // Check if this is an invitation signup
    const pendingInvitation = sessionStorage.getItem("pendingInvitation");
    if (pendingInvitation) {
      try {
        const invitation = JSON.parse(pendingInvitation);
        setInvitationData(invitation);
        console.log("Loading invitation data:", invitation);
        
        // Pre-fill form with invitation data
        if (invitation.firstName) setFirstName(invitation.firstName);
        if (invitation.lastName) setLastName(invitation.lastName);
        if (invitation.phone) setPhone(invitation.phone);
        if (invitation.jobTitle) setJobTitle(invitation.jobTitle);
      } catch (error) {
        console.error("Error parsing invitation data:", error);
        sessionStorage.removeItem("pendingInvitation");
      }
    }
  }, []);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      console.log("[Signup] Starting signup process for:", email);
      console.log("[Signup] Invitation data:", invitationData);

      // Validate invitation data if present
      if (invitationData) {
        // contactId is optional: direct/sub-organisation invitations have no CRM contact record
        if (!invitationData.token) {
          console.error("[Signup] ❌ Incomplete invitation data:", {
            hasToken: !!invitationData.token,
            hasContactId: !!invitationData.contactId,
            token: invitationData.token,
            contactId: invitationData.contactId
          });
          throw new Error("Invalid invitation data. Please use the invitation link again.");
        }
        console.log("[Signup] ✅ Invitation data validated");
      }

      // Build metadata object
      const signupMetadata = {
        username: username,
        first_name: firstName,
        last_name: lastName,
        phone: phone || undefined,
        job_title: jobTitle || undefined,
        // Include invitation data for the trigger function
        ...(invitationData && {
          invitation_token: invitationData.token,
          contact_id: invitationData.contactId || undefined,
          organization_id: invitationData.organizationId,
          invited_role: invitationData.invitedRole
        })
      };

      console.log("[Signup] 📤 Sending metadata to Supabase:", JSON.stringify(signupMetadata, null, 2));

      // Sign up the user with all metadata including invitation data
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: signupMetadata
        }
      });
      
      console.log("[Signup] Auth signup response:", { data, error });

      if (error) {
        console.error("[Signup] Auth signup error:", error);
        throw error;
      }

      if (data.user) {
        console.log("[Signup] User created successfully:", data.user.id);

        // If invitation signup, assign devices FIRST, then mark invitation as accepted
        if (invitationData) {
          console.log("[Signup] Processing invitation for user:", data.user.id);
          
          try {
            // Check if this is a proposal invitation or regular invitation
            if (invitationData.proposalToken) {
              // This is a proposal signup - don't update user_invitations table
              console.log("[Signup] Proposal invitation signup completed");
            } else {
              // Regular invitation - FIRST assign devices, THEN mark as accepted
              console.log("[Signup] Fetching invitation details for device assignment");
              
              // Step 1: Fetch the invitation to get device_ids
              const { data: invitation, error: fetchError } = await supabase
                .from('user_invitations')
                .select('device_ids')
                .eq('id', invitationData.token)
                .single();
              
              if (fetchError) {
                console.error("[Signup] Error fetching invitation details:", fetchError);
              } else if (invitation?.device_ids && invitation.device_ids.length > 0) {
                // Step 2: Create device assignments
                console.log("[Signup] 📱 Assigning devices:", invitation.device_ids);
                
                const deviceAssignments = invitation.device_ids.map((deviceId: string) => ({
                  user_id: data.user!.id,
                  device_id: deviceId
                }));

                const { error: assignmentError } = await supabase
                  .from('device_assignments')
                  .insert(deviceAssignments);

                if (assignmentError) {
                  console.error("[Signup] Error creating device assignments:", assignmentError);
                } else {
                  console.log("[Signup] ✅ Devices assigned successfully:", invitation.device_ids.length);
                }
              } else {
                console.log("[Signup] No devices to assign for this invitation");
              }
              
              // Step 3: NOW mark invitation as accepted (after device assignment)
              const { error: inviteUpdateError } = await supabase
                .from('user_invitations')
                .update({ status: 'accepted' })
                .eq('id', invitationData.token);

              if (inviteUpdateError) {
                console.error("[Signup] Error updating invitation status:", inviteUpdateError);
              } else {
                console.log("[Signup] ✅ Invitation marked as accepted");
              }
            }
          } catch (inviteError) {
            console.error("[Signup] Exception processing invitation:", inviteError);
            // Don't throw here as the user was created successfully
          }

          sessionStorage.removeItem("pendingInvitation");
        }

        toast({
          title: "Account created successfully",
          description: invitationData 
            ? (invitationData.proposalToken 
              ? "Account created! You can now view your proposal." 
              : "Your invitation has been accepted and account created.")
            : "Please check your email to verify your account.",
        });

        // For proposal signups, redirect to the proposal after successful signup
        if (invitationData?.proposalToken) {
          setTimeout(() => {
            window.location.href = `/proposal/${invitationData.proposalToken}`;
          }, 1000);
        } else {
          // Pass the typed form values to onSuccess callback
          onSuccess({
            firstName,
            lastName,
            phone,
            jobTitle
          });
        }
      } else {
        throw new Error("No user returned from signup.");
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create account",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSignup} className="space-y-4">
      {invitationData && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-center">
            <p className="text-sm text-blue-800 font-medium mb-1">
              {invitationData.proposalToken 
                ? "🎉 Creating account to view proposal" 
                : "🎉 Creating account from invitation"}
            </p>
            <p className="text-xs text-blue-700">
              {invitationData.proposalToken
                ? `You'll be added to ${invitationData.organizationName || 'the organization'} with ${invitationData.invitedRole} role`
                : `You'll be added to the organization with ${invitationData.invitedRole} role`}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          type="email"
          value={email}
          disabled
          className="bg-gray-50"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <div className="relative">
          <Input
            id="signup-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isProcessing}
            placeholder="Create a strong password"
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            disabled={isProcessing}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeOff size={16} />
            ) : (
              <Eye size={16} />
            )}
          </button>
        </div>
        
        {password && (
          <div className="mt-3 p-3 bg-muted rounded-lg">
            <div className="text-xs font-medium mb-2 text-muted-foreground">Password Requirements:</div>
            <div className="space-y-1">
              <div className={`flex items-center text-xs ${passwordValidation.requirements.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                <span className="mr-2">{passwordValidation.requirements.minLength ? '✓' : '○'}</span>
                At least 10 characters
              </div>
              <div className={`flex items-center text-xs ${passwordValidation.requirements.hasLowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                <span className="mr-2">{passwordValidation.requirements.hasLowercase ? '✓' : '○'}</span>
                One lowercase letter (a-z)
              </div>
              <div className={`flex items-center text-xs ${passwordValidation.requirements.hasUppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                <span className="mr-2">{passwordValidation.requirements.hasUppercase ? '✓' : '○'}</span>
                One uppercase letter (A-Z)
              </div>
              <div className={`flex items-center text-xs ${passwordValidation.requirements.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                <span className="mr-2">{passwordValidation.requirements.hasNumber ? '✓' : '○'}</span>
                One number (0-9)
              </div>
              <div className={`flex items-center text-xs ${passwordValidation.requirements.hasSpecial ? 'text-green-600' : 'text-muted-foreground'}`}>
                <span className="mr-2">{passwordValidation.requirements.hasSpecial ? '✓' : '○'}</span>
                One special character (!@#$%^&*)
              </div>
              {passwordValidation.requirements.isPassphrase && (
                <div className="flex items-center text-xs text-blue-600">
                  <span className="mr-2">✓</span>
                  Strong passphrase (16+ characters)
                </div>
              )}
            </div>
            
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordValidation.score <= 2 ? 'bg-red-500' : 
                      passwordValidation.score <= 4 ? 'bg-yellow-500' : 
                      'bg-green-500'
                    }`}
                    style={{ width: `${(passwordValidation.score / 6) * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  passwordValidation.score <= 2 ? 'text-red-600' : 
                  passwordValidation.score <= 4 ? 'text-yellow-600' : 
                  'text-green-600'
                }`}>
                  {passwordValidation.isStrong ? 'Strong' : 
                   passwordValidation.score <= 2 ? 'Weak' : 'Fair'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-confirm-password">Confirm Password</Label>
        <div className="relative">
          <Input
            id="signup-confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isProcessing}
            placeholder="Confirm your password"
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            disabled={isProcessing}
            aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
          >
            {showConfirmPassword ? (
              <EyeOff size={16} />
            ) : (
              <Eye size={16} />
            )}
          </button>
        </div>
        {confirmPassword && !passwordsMatch && (
          <p className="text-xs text-red-600">Passwords do not match</p>
        )}
        {confirmPassword && passwordsMatch && (
          <p className="text-xs text-green-600">✓ Passwords match</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-username">Username</Label>
        <Input
          id="signup-username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={isProcessing}
          placeholder="Choose a username"
          pattern="^[a-zA-Z0-9_-]+$"
          title="Username can only contain letters, numbers, underscores, and hyphens"
        />
        <p className="text-xs text-muted-foreground">
          This will be your unique username. Only letters, numbers, underscores, and hyphens allowed.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="signup-firstName">First Name</Label>
          <Input
            id="signup-firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={isProcessing}
            placeholder="First name"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-lastName">Last Name</Label>
          <Input
            id="signup-lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={isProcessing}
            placeholder="Last name"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-phone">Phone (optional)</Label>
        <Input
          id="signup-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={isProcessing}
          placeholder="Phone number"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-jobTitle">Job Title (optional)</Label>
        <Input
          id="signup-jobTitle"
          type="text"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          disabled={isProcessing}
          placeholder="Job title"
        />
      </div>

      <Button type="submit" className={`w-full ${customButtonClass}`} disabled={!canSubmit}>
        {isProcessing ? 'Creating Account...' : 'Create Account'}
      </Button>
      
      {!canSubmit && !isProcessing && (
        <p className="text-xs text-muted-foreground text-center">
          Please ensure your password meets all requirements and both password fields match.
        </p>
      )}
    </form>
  );
}
